import { motion } from 'framer-motion';
import { Diamond, HardDrive, FolderOpen, Archive, MonitorDown, CheckCircle2 } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const STEPS = [
  { number: '01', icon: MonitorDown, title: 'Complete Your Purchase', description: "After completing your purchase via our Secure Checkout, you'll receive an email with your Google Drive access link within 60 seconds." },
  { number: '02', icon: HardDrive, title: 'Access the 900GB+ Drive', description: 'Open the shared Google Drive folder. All 4,000+ files are organized into 8 category folders. You can browse or download everything.' },
  { number: '03', icon: Archive, title: 'Download & Extract', description: 'Files are compressed in .RAR/.ZIP format for faster downloads. Extract using WinRAR (Windows) or 7-Zip (free, open-source) on any OS.' },
  { number: '04', icon: FolderOpen, title: 'Open in Your Software', description: 'Import .STL, .OBJ, or .STEP files directly into Rhino, ZBrush, Blender, or any 3D software. Files are print-ready — no cleanup needed.' },
];

export default function StepByStepGuide() {
  return (
    <section data-testid="guide-section" className="relative py-20 sm:py-28" style={{ background: '#FFF7CD' }}>
      <div className="relative z-10 w-full max-w-3xl mx-auto px-6 sm:px-8">
        <motion.span custom={0} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="uppercase text-xs tracking-[0.25em] font-bold text-[#c88a5e] mb-4 block font-body" data-testid="guide-overline">
          <Diamond className="inline-block w-3 h-3 mr-2 -mt-0.5" /> How It Works
        </motion.span>
        <motion.h2 custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="font-heading font-bold text-[#111] tracking-tight mb-12" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}
          data-testid="guide-headline">
          Step-by-Step Guide
        </motion.h2>

        <div data-testid="guide-steps-container">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div key={step.number} custom={i + 2} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="relative flex gap-5" data-testid={`guide-step-${i}`}>
                {i < STEPS.length - 1 && <div className="absolute left-[19px] top-12 bottom-0 w-px" style={{ background: 'rgba(253,195,161,0.3)' }} />}
                <div className="w-10 h-10 shrink-0 flex items-center justify-center relative z-10 rounded-xl" style={{ background: 'rgba(253,195,161,0.15)' }}>
                  <Icon className="w-4 h-4 text-[#c88a5e]" />
                </div>
                <div className="pb-10">
                  <span className="font-body text-[10px] uppercase tracking-widest text-[#c88a5e] font-bold block mb-1.5">Step {step.number}</span>
                  <h4 className="font-heading font-bold text-[#111] text-base mb-2">{step.title}</h4>
                  <p className="font-body text-sm text-[#777] leading-relaxed max-w-md">{step.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div custom={7} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="mt-4 card-elevated p-5" data-testid="guide-pro-tips">
          <span className="font-body text-xs font-bold text-[#c88a5e] block mb-3">Pro Tips</span>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#FDC3A1] mt-0.5 shrink-0" />
              <span className="font-body text-xs text-[#666]">Use <span className="text-[#111] font-medium">7-Zip</span> (free) if you don't have WinRAR — it handles all archive formats.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#FDC3A1] mt-0.5 shrink-0" />
              <span className="font-body text-xs text-[#666]">Download individual folders first if your internet is slow — no need to grab all 900GB at once.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#FDC3A1] mt-0.5 shrink-0" />
              <span className="font-body text-xs text-[#666]">Star the Drive folder in Google Drive for quick access later.</span>
            </li>
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
